"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __exportStar = (this && this.__exportStar) || function(m, exports) {
    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.OptionFromZeroOr = exports.StructFromPartial = exports.EncodePadStart = exports.CapturePattern = exports.BooleanFromBit = exports.PrintString = exports.Singleton = void 0;
exports.encodedLiterals = encodedLiterals;
exports.FromCaseAgnostic = FromCaseAgnostic;
exports.makeEncodedBoundArbitrary = makeEncodedBoundArbitrary;
exports.makeAlias = makeAlias;
exports.makeNonEmptySetFromSplitString = makeNonEmptySetFromSplitString;
const effect_1 = require("effect");
const utils_1 = require("./utils");
__exportStar(require("effect/Schema"), exports);
const NAMESPACE = 'Schema';
/**
 * Given a Schema for a value, returns a Schema for an object that wraps the
 * value in a property with the given key.
 *
 * @example
 *   const AnswerFromNumber = S.Number.pipe(S.Singleton('answer'));
 *   S.decodeSync(AnswerFromNumber)(42); // => { answer: 42 }
 */
const Singleton = (k) => (Value) => Value.pipe(effect_1.Schema.transformOrFail(effect_1.Schema.Struct({ [k]: Value }), {
    strict: true,
    decode: value => effect_1.Schema.encode(Value)(value).pipe(effect_1.Effect.mapBoth({
        onSuccess: v => ({ [k]: v }),
        onFailure: e => e.issue,
    })),
    encode: value => effect_1.Schema.decode(Value)(value[k]).pipe(effect_1.Effect.mapError(e => e.issue)),
})).annotations({ identifier: `${NAMESPACE}.Singleton<${k}>` });
exports.Singleton = Singleton;
const printStringRegex = /^[ -~]*$/;
exports.PrintString = effect_1.Schema.String.pipe(effect_1.Schema.pattern(printStringRegex, {
    message: ({ actual }) => {
        if (typeof actual !== 'string') {
            return 'Is not a string';
        }
        const violationMap = Array.from(actual).reduce((acc, character, index) => printStringRegex.test(character) ? acc : acc.set(index, character), new Map());
        const MAX_PRINTED_VIOLATION_COUNT = 10;
        return ('Contains invalid characters.\nViolations:\n' +
            Array.from(violationMap.entries())
                .slice(0, MAX_PRINTED_VIOLATION_COUNT)
                .map(([index, character]) => `-> Character "${character}" @ [${index}]`)
                .join('\n')
                .concat(violationMap.size > MAX_PRINTED_VIOLATION_COUNT
                ? `${violationMap.size - MAX_PRINTED_VIOLATION_COUNT} more violations...`
                : ''));
    },
})).annotations({
    identifier: 'Schema.PrintString',
    description: 'A string containing only accent-free alphabetic letters (uppercase and lowercase), spaces, simple symbols, and digits. ' +
        'This means that only characters with an ASCII value between 0x20 (SPACE) and 0x7E (TILDE) (inclusive) are accepted.',
    documentation: 'https://www.rapidtables.com/code/text/ascii-table.html',
});
exports.BooleanFromBit = effect_1.Schema.transformLiterals([0, false], [1, true]).annotations({
    identifier: `${NAMESPACE}.BooleanFromBit`,
    description: 'Transforms 0 and 1 into false and true respectively',
});
/**
 * {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Regular_expressions/Named_capturing_group MDN: Named capturing group}
 *
 * @param regex The regular expression to match
 * @param Schema A schema with an encoded side of { [capturingGroupName: (string)]: subpatternMatch (string)  }
 * @param encode Encode function to use for this transform schema. If not provided, all attempts to encode will fail.
 * @returns A Schema that transforms between a string and a record associating
 * each capturing group name within the {@link regex} to its subpattern match.
 */
const CapturePattern = (regex, Schema, encode = (toI, _options, ast, _toA) => effect_1.Effect.fail(new effect_1.ParseResult.Forbidden(ast, toI, 'Can not encode CapturePattern: An encode function was not provided.'))) => {
    // Keys on encoded side of `Schema` parameter
    const fromKeys = (0, effect_1.pipe)(Schema, effect_1.Schema.encodedBoundSchema, effect_1.Struct.get('ast'), effect_1.SchemaAST.getPropertySignatures, effect_1.Array.map(effect_1.Struct.get('name')));
    return effect_1.Schema.transformOrFail(effect_1.Schema.String.pipe(effect_1.Schema.pattern(regex)), Schema, {
        strict: true,
        decode: (string, _parseOptions, ast) => {
            // The following line ensures that `lastIndex` is reset to `0` in case the user has specified the `g` flag
            regex.lastIndex = 0;
            const exec = regex.exec(string);
            // In reality, `exec` can never be null,
            // because the regex will already have matched the string within the `S.pattern` refinement
            // TypeScript just doesn't know this, so we have to use the optional chaining operator here
            const groups = exec?.groups ?? {};
            const missingNamedCapturingGroupNames = effect_1.Array.difference(fromKeys, effect_1.Record.keys(groups));
            if (effect_1.Array.isNonEmptyReadonlyArray(missingNamedCapturingGroupNames))
                return effect_1.Effect.fail(new effect_1.ParseResult.Forbidden(ast, string, `The provided regular expression (\`${regex.source}\`) ` +
                    `does not contain some of the expected named capturing groups:\n` +
                    missingNamedCapturingGroupNames.map(name => `\`${String(name)}\``).join(', ')));
            return effect_1.Effect.succeed(groups);
        },
        encode,
    });
};
exports.CapturePattern = CapturePattern;
const EncodePadStart = (maxLength, fillString) => effect_1.Schema.transform(effect_1.Schema.String, effect_1.Schema.String, {
    strict: true,
    decode: a => a,
    encode: i => i.padStart(maxLength, fillString),
}).annotations({ identifier: `${NAMESPACE}.EncodePadStart<${maxLength},${fillString}>` });
exports.EncodePadStart = EncodePadStart;
/**
 * Utility function for constructing a transform schema that will decode a struct `A` from `Partial<A>` using the provided `defaults` as fallback.
 */
const StructFromPartial = (defaults) => (schema) => {
    const partialSchema = effect_1.Schema.partialWith(schema, { exact: true });
    return effect_1.Schema.transform(partialSchema, effect_1.Schema.typeSchema(schema), {
        strict: true,
        decode: partial => ({ ...defaults, ...partial }),
        encode: full => full,
    });
};
exports.StructFromPartial = StructFromPartial;
/**
 * Utility function for constructing a schema that transforms constituents of the provided schema
 * into `Option.Some<T>`, while decoding non-constituents into `Option.None`.
 */
const OptionFromZeroOr = (value) => effect_1.Schema.transform(effect_1.Schema.Union(effect_1.Schema.Literal(0), value), effect_1.Schema.OptionFromSelf(effect_1.Schema.typeSchema(effect_1.Schema.asSchema(value))), {
    strict: true,
    decode: i => (i === 0 ? effect_1.Option.none() : effect_1.Option.some(i)),
    encode: a => a.pipe(effect_1.Option.getOrElse(() => 0)),
});
exports.OptionFromZeroOr = OptionFromZeroOr;
function encodedLiterals(schema) {
    return schema.members.map(({ from: { literals: [from], }, }) => from);
}
function FromCaseAgnostic(...literals) {
    return effect_1.Schema.Union(...literals.map(literal => {
        const fromRegExp = new RegExp((0, effect_1.pipe)(literal, utils_1.Regex.Pattern.escape, utils_1.Regex.Pattern.absolute), 'i');
        const from = effect_1.Schema.String.pipe(effect_1.Schema.pattern(fromRegExp));
        const to = effect_1.Schema.Literal(literal);
        return effect_1.Schema.transform(from, to, {
            decode: effect_1.Function.constant(literal),
            encode: effect_1.identity,
        });
    }));
}
/**
 * Some dynamic `Schema`s can have an encoded side that is much too wide when using
 * `Arbitrary.make(S.encodedBoundSchema(schema))` to create an `Arbitrary`.
 *
 * As a workaround, we can create an `Arbitrary` for the (narrow) decoded side of the schema,
 * and encode the values produced by this `Arbitrary`.
 *
 * @param schema The original `Schema`
 * @returns an `Arbitrary` producing encoded representations of `A`
 */
function makeEncodedBoundArbitrary(schema) {
    return effect_1.Arbitrary.make(schema).map(effect_1.Schema.encodeSync(schema));
}
/**
 * Utility function to create a schema that provides (legacy) aliases on the encoded side (`from`)
 * for the singular `to` value on the decoded side
 */
function makeAlias(to, from) {
    return effect_1.Schema.Union(effect_1.Schema.Literal(to), effect_1.Schema.transformLiterals(...from.map(from => [from, to])));
}
function makeNonEmptySetFromSplitString(schema, separator) {
    return (0, effect_1.pipe)(effect_1.Schema.split(separator), effect_1.Schema.compose(effect_1.Schema.Set(schema)), effect_1.Schema.filter(set => set.size > 0));
}
//# sourceMappingURL=schema.js.map